import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings2 } from 'lucide-react';
import LegalLayout, { TocEntry } from './LegalLayout';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { company, legalPaths } from '../data/company';
import { CONSENT_TTL_DAYS, openCookieSettings } from '../lib/consent';

const TOC: TocEntry[] = [
  { id: 'summary', label: 'In short' },
  { id: 'what-are-cookies', label: 'What cookies are' },
  { id: 'legal-basis', label: 'The rules we follow' },
  { id: 'categories', label: 'Categories we use' },
  { id: 'inventory', label: 'Cookie inventory' },
  { id: 'consent', label: 'How consent works here' },
  { id: 'withdraw', label: 'Changing or withdrawing consent' },
  { id: 'browser', label: 'Browser and device controls' },
  { id: 'third-parties', label: 'Third parties' },
  { id: 'transfers', label: 'Transfers outside the EEA' },
  { id: 'rights', label: 'Your rights' },
  { id: 'changes', label: 'Changes to this policy' },
  { id: 'contact', label: 'Contact us' },
];

const CookiePolicy: React.FC = () => {
  useDocumentTitle(
    'Cookie Policy — Parnasa Apps',
    'What Parnasa Apps stores on your device, why, for how long, and how to change or withdraw your consent.'
  );

  return (
    <LegalLayout
      kicker="Legal · Cookies & tracking"
      title="Cookie Policy"
      intro="Exactly what is stored on your device when you visit this website, why, for how long, and how to change your mind — written for the EU ePrivacy Directive, the GDPR and the Italian Garante's cookie guidelines."
      toc={TOC}
    >
      <h2 id="summary">In short</h2>
      <p>
        This is an informational website, and we keep the storage it uses to a minimum. In practical
        terms:
      </p>
      <ul>
        <li>
          <strong>Nothing optional is set before you agree.</strong> No analytics, no marketing, no
          profiling on your first page view.
        </li>
        <li>
          <strong>Refusing is as easy as accepting.</strong> &ldquo;Reject all&rdquo; sits next to
          &ldquo;Accept all&rdquo; with the same prominence, and no part of the site is withheld if you
          refuse.
        </li>
        <li>
          <strong>Every non-essential category starts switched off.</strong> There are no pre-ticked
          boxes, and closing the banner is not treated as consent.
        </li>
        <li>
          <strong>Your choice is not permanent.</strong> It expires after {CONSENT_TTL_DAYS} days, and
          you can change it any time from &ldquo;Cookie settings&rdquo; in the footer.
        </li>
        <li>
          <strong>We do not sell data</strong> and we do not share cookie data with advertising
          networks.
        </li>
      </ul>

      <div className="my-8 rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7 not-prose">
        <div className="flex items-start gap-3">
          <Cookie size={18} strokeWidth={1.6} className="mt-0.5 shrink-0 text-peri-300" />
          <div>
            <p className="text-[15px] font-semibold text-paper">Manage your choice now</p>
            <p className="mt-2 text-[14px] leading-relaxed text-paper/60">
              Open the preference panel to review each category and save a new decision. It applies
              immediately.
            </p>
            <button
              type="button"
              onClick={openCookieSettings}
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-[10.5px] uppercase tracking-label text-paper transition-colors hover:border-peri-400/60 hover:bg-peri-400/10"
            >
              <Settings2 size={13} strokeWidth={1.7} />
              Open cookie settings
            </button>
          </div>
        </div>
      </div>

      <h2 id="what-are-cookies">What cookies are</h2>
      <p>
        A cookie is a small text file that a website asks your browser to store on your device. On a
        later request the browser sends it back, which lets the site recognise the same browser — for
        example to keep you logged in, remember a language, or count a visit.
      </p>
      <p>
        This policy also covers <strong>similar technologies</strong> that are treated the same way in
        law: <em>local storage</em> and <em>session storage</em> (data kept by the browser without
        being sent on every request), <em>pixels</em> and <em>web beacons</em> (tiny images used to
        register that a page or email was opened), and <em>device fingerprinting</em> techniques.
      </p>
      <p>Cookies are usually described along two axes:</p>
      <ul>
        <li>
          <strong>Who sets them</strong> — <em>first-party</em> cookies come from the site you are
          visiting; <em>third-party</em> cookies come from another domain embedded in the page.
        </li>
        <li>
          <strong>How long they last</strong> — <em>session</em> cookies are erased when you close the
          browser; <em>persistent</em> cookies remain until they expire or you delete them.
        </li>
      </ul>

      <h2 id="legal-basis">The rules we follow</h2>
      <p>Storage on your device is governed by two overlapping sets of rules, and we apply both:</p>
      <ul>
        <li>
          <strong>ePrivacy Directive 2002/58/EC, Article 5(3)</strong>, as implemented in Italy by
          Article 122 of Legislative Decree 196/2003 (the Codice Privacy). Consent is required before
          storing or accessing information on your device, unless the storage is strictly necessary to
          deliver a service you requested, or is used solely to carry out a transmission.
        </li>
        <li>
          <strong>GDPR (EU) 2016/679</strong>, which defines what valid consent is: freely given,
          specific, informed and unambiguous, given by a clear affirmative act, as easy to withdraw as
          to give, and demonstrable by us (Articles 4(11) and 7).
        </li>
        <li>
          <strong>Garante guidelines on cookies and other tracking tools</strong> (10 June 2021), which
          add practical requirements we have followed: no consent by scrolling, no cookie walls,
          granular categories, an equally prominent refusal option, and a fresh request after at most
          six months.
        </li>
        <li>
          <strong>EDPB Guidelines 05/2020 on consent</strong>, on the standard for a genuine, unbundled
          choice.
        </li>
      </ul>
      <p>
        If you are outside the EEA, we apply the same standard to you. It is a good baseline, and it
        keeps things simple.
      </p>

      <h2 id="categories">Categories we use</h2>

      <h4>Strictly necessary — always active</h4>
      <p>
        These make the site work: they remember the cookie choice you just made, help keep the site
        secure, balance traffic across servers and preserve accessibility settings. They store no
        information about your interests and cannot be switched off, because without them the service
        you asked for cannot be delivered. Under Article 122 they are exempt from consent.
      </p>

      <h4>Preferences — off by default</h4>
      <p>
        These remember choices such as language or region so you do not have to set them again. Useful,
        but not essential: they are set only if you allow the category.
      </p>

      <h4>Analytics — off by default</h4>
      <p>
        These help us understand in aggregate which pages are read and where visitors struggle, so we
        can improve the site. We configure analytics for measurement, not for advertising: no
        cross-site tracking, no selling of data. They are set only with your consent, and refusing them
        changes nothing about what you can see.
      </p>

      <h4>Marketing — off by default, and unused today</h4>
      <p>
        This category would cover measuring a campaign or reaching an audience elsewhere.{' '}
        <strong>We currently run no marketing or advertising cookies at all.</strong> The switch stays
        in the panel so that if we ever add one, it is your decision and not a surprise — and the
        inventory below will be updated before anything is set.
      </p>

      <h2 id="inventory">Cookie inventory</h2>
      <p>
        This is the complete list of what this website stores today. We keep it accurate; if we add a
        tool, this table changes before the tool goes live.
      </p>
      <div className="table-wrap">
        <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type &amp; party</th>
            <th>Purpose</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>parnasa:cookie-consent</td>
            <td>Local storage · first party · strictly necessary</td>
            <td>
              Records which categories you allowed, when, and by which action, so we can honour your
              choice and demonstrate it as Article 7(1) GDPR requires
            </td>
            <td>{CONSENT_TTL_DAYS} days</td>
          </tr>
          <tr>
            <td>Hosting &amp; CDN operational cookies</td>
            <td>Cookie · first party · strictly necessary</td>
            <td>
              Load balancing, request routing and protection against abuse, set by the infrastructure
              serving this site
            </td>
            <td>Session, or up to 24 hours</td>
          </tr>
          <tr>
            <td>Preference cookies</td>
            <td>Cookie or local storage · first party · preferences</td>
            <td>
              Remember interface choices such as language. Set only if you enable the Preferences
              category
            </td>
            <td>Up to 12 months</td>
          </tr>
          <tr>
            <td>Analytics cookies</td>
            <td>Cookie or local storage · first or third party · analytics</td>
            <td>
              Aggregated statistics on pages viewed and referrers. Set only if you enable the Analytics
              category
            </td>
            <td>Up to 13 months</td>
          </tr>
          <tr>
            <td>Marketing cookies</td>
            <td>—</td>
            <td>None in use. Nothing is set under this category at present</td>
            <td>—</td>
          </tr>
        </tbody>
        </table>
      </div>
      <p>
        The consent record is kept in your browser&rsquo;s local storage rather than in a cookie, so it
        is never transmitted with your requests. It contains only your category choices, a version
        number and a timestamp — no identifier, and nothing that identifies you personally.
      </p>

      <h2 id="consent">How consent works here</h2>
      <ul>
        <li>
          <strong>First visit.</strong> A notice appears explaining the categories, with
          &ldquo;Accept all&rdquo;, &ldquo;Reject all&rdquo; and &ldquo;Manage preferences&rdquo;.
          Nothing optional is stored while it is open.
        </li>
        <li>
          <strong>No dark patterns.</strong> Accept and reject are styled with equal weight. There is no
          cookie wall, and content is not blurred or withheld to pressure you.
        </li>
        <li>
          <strong>Scrolling is not consent.</strong> Neither is continuing to browse, nor closing the
          notice. Only clicking an explicit button records a choice.
        </li>
        <li>
          <strong>Granularity.</strong> In &ldquo;Manage preferences&rdquo; each category has its own
          switch, all optional ones off, and you can save any combination.
        </li>
        <li>
          <strong>Proof of consent.</strong> We store the categories chosen, the action taken (accept
          all, reject all or custom), a timestamp and the policy version, so we can demonstrate consent
          as required.
        </li>
        <li>
          <strong>Renewal.</strong> After {CONSENT_TTL_DAYS} days — within the six months the Garante
          allows — the notice returns so the choice stays current. It also returns if we materially
          change the categories.
        </li>
      </ul>

      <h2 id="withdraw">Changing or withdrawing consent</h2>
      <p>
        Withdrawal is as easy as consent, as Article 7(3) GDPR requires. Choose whichever you prefer:
      </p>
      <ul>
        <li>
          Select <strong>Cookie settings</strong> in the footer of any page, adjust the switches and
          save. The change applies immediately.
        </li>
        <li>Use the button near the top of this page to open the same panel.</li>
        <li>
          Clear cookies and site data for {company.domain} in your browser. This deletes the stored
          record, so the notice will appear again on your next visit.
        </li>
      </ul>
      <p>
        Withdrawing consent stops future processing based on it. It does not make the processing that
        already took place unlawful, and any data already collected under the Analytics category is
        retained only for the period stated above unless you ask us to delete it sooner.
      </p>

      <h2 id="browser">Browser and device controls</h2>
      <p>
        Independently of our banner, your browser can block or delete cookies, either generally or per
        site. Blocking all cookies may break parts of many websites, including this one. The vendors
        document their own settings:
      </p>
      <ul>
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/kb/enhanced-tracking-protection-firefox-desktop"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
            target="_blank"
            rel="noopener noreferrer"
          >
            Apple Safari
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/microsoft-edge"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Edge
          </a>
        </li>
      </ul>
      <p>
        We also respect the <strong>Global Privacy Control</strong> and <strong>Do Not Track</strong>{' '}
        signals where a browser sends them: we treat either as a refusal of all optional categories.
      </p>

      <h2 id="third-parties">Third parties</h2>
      <p>
        No third-party advertising, social or profiling scripts are embedded in this website. There are
        no social media &ldquo;like&rdquo; buttons, no embedded video players and no chat widgets, all
        of which typically set cookies of their own.
      </p>
      <p>
        If we later embed a third-party component, we will list it in the inventory above with its
        provider, purpose and cookie durations, and — where it is not strictly necessary — load it only
        after you have consented to its category.
      </p>

      <h2 id="transfers">Transfers outside the EEA</h2>
      <p>
        Where a consented tool involves transferring data outside the European Economic Area, we rely on
        the safeguards in Chapter V GDPR: an adequacy decision under Article 45, or Standard
        Contractual Clauses with a transfer impact assessment under Article 46. Details for any specific
        tool are available on request, and the{' '}
        <Link to={legalPaths.privacy}>Privacy Policy</Link> explains the general approach.
      </p>

      <h2 id="rights">Your rights</h2>
      <p>
        Cookie identifiers can constitute personal data, so the rights in the GDPR apply to them: access,
        rectification, erasure, restriction, portability, objection and withdrawal of consent. The{' '}
        <Link to={legalPaths.privacy}>Privacy Policy</Link> sets out each right and how to exercise it,
        and you may lodge a complaint with the{' '}
        <a href={company.supervisoryAuthority.url} target="_blank" rel="noopener noreferrer">
          {company.supervisoryAuthority.name}
        </a>{' '}
        under Article 77 GDPR.
      </p>

      <h2 id="changes">Changes to this policy</h2>
      <p>
        We update this policy whenever our use of cookies changes, and always before a new tracking tool
        is activated. The date at the top of the page shows the current version. If a change requires
        consent, we ask again rather than assuming your earlier choice still applies.
      </p>

      <h2 id="contact">Contact us</h2>
      <p>Questions about cookies, or about a specific entry in the inventory:</p>
      <ul>
        <li>
          <strong>Email:</strong> <a href={`mailto:${company.email}`}>{company.email}</a>
        </li>
        <li>
          <strong>Post:</strong> {company.legalName}, {company.addressOneLine}
        </li>
      </ul>
    </LegalLayout>
  );
};

export default CookiePolicy;
